/**
 * `POST /api/events` — the first-party event contract (spec sections 14, 16).
 *
 * Spec section 14 states the rule this file exists to enforce: "Allow list event
 * names and properties. **Never accept an arbitrary PII blob.**" An events
 * endpoint is the easiest place in a codebase to accidentally build a
 * general-purpose store for whatever the browser felt like sending, and the
 * browser is the one part of this system an attacker controls completely.
 *
 * So everything here is a closed set:
 *
 * - **Event names** are the nine from spec section 16. Not a string, not a
 *   pattern — a union.
 * - **Property keys** are an allow list, derived from what `track()` callers
 *   actually pass (src/client/lib/analytics.ts). An unknown key is a rejection,
 *   not a silently dropped field, so a caller that starts sending something new
 *   fails loudly in a test rather than quietly on production.
 * - **Property values** are short scalars with per-key caps. No objects, no
 *   arrays, no nesting.
 * - **There is no key for an email address, a name, a phone number, or a ZIP**,
 *   and there never will be. Those live on the lead, written by the signup
 *   endpoint, from a validated form. An event describes an interaction — which
 *   form, which audience, which field failed validation — never what was typed
 *   into it (spec section 19).
 *
 * `leadId` is deliberately absent from the wire format. Events are attributed
 * by session id, which the server has no way to resolve to a person, and the
 * browser is never given a lead id to send back.
 */

import { z } from 'zod';

/** Spec section 16, "Tracked events". This union is the allow list. */
export const EVENT_NAMES = [
  'audience_selected',
  'cta_clicked',
  'form_started',
  'form_validation_error',
  'owner_signup_completed',
  'provider_signup_completed',
  'referral_started',
  'referral_completed',
  'share_link_copied',
] as const;

export type EventName = (typeof EVENT_NAMES)[number];

/**
 * Allowed property keys and the maximum length of each value.
 *
 * Every entry is here because a `track()` call site sends it:
 *
 * - `audience`, `form`, `referrer` — 'owner' or 'provider'. `referrer` is who
 *   made a recommendation, not an HTTP referrer, and carries no URL.
 * - `placement` — where on the page a CTA was, e.g. 'hero'.
 * - `source` — 'client' or 'server', which side rejected a form.
 * - `fields` — the *names* of the fields that failed validation, comma
 *   separated. Names only. The values that failed are never sent, which is the
 *   single most important line in this file.
 * - `variant` — the landing A/B assignment, 'a' or 'b'. Attached to every
 *   event by the transport rather than by a call site, so the dimension is on
 *   the whole funnel and not only on the events somebody remembered to tag.
 *
 * Adding a key means adding it here first, deliberately.
 */
export const EVENT_PROPERTY_LIMITS = {
  audience: 32,
  form: 32,
  referrer: 32,
  placement: 64,
  source: 32,
  fields: 200,
  variant: 8,
} as const;

export type EventPropertyKey = keyof typeof EVENT_PROPERTY_LIMITS;

export const EVENT_PROPERTY_KEYS = Object.keys(EVENT_PROPERTY_LIMITS) as EventPropertyKey[];

/** Caps on the two free-form-ish fields that are not properties. */
export const EVENT_LIMITS = {
  path: 512,
  sessionId: 64,
} as const;

/**
 * A property value: a short string, a finite number, or a boolean. Numbers are
 * bounded so a payload cannot smuggle a large float, and non-integers are
 * rejected because nothing this site tracks is fractional.
 */
function propertyValue(max: number) {
  return z.union([
    z.string().trim().min(1).max(max),
    z.number().int().gte(-1_000_000).lte(1_000_000),
    z.boolean(),
  ]);
}

/**
 * The property bag. `.strict()` is the point: an unknown key fails the request
 * rather than being stripped, so nothing can be smuggled through by a client
 * that thinks it knows better, and a typo in a `track()` call is a test
 * failure rather than a missing column in six weeks' analysis.
 */
export const eventPropertiesSchema = z
  .object(
    Object.fromEntries(
      EVENT_PROPERTY_KEYS.map((key) => [key, propertyValue(EVENT_PROPERTY_LIMITS[key]).optional()]),
    ) as {
      [K in EventPropertyKey]: z.ZodOptional<ReturnType<typeof propertyValue>>;
    },
  )
  .strict();

/**
 * An opaque per-session id, generated in the browser (`crypto.randomUUID()` in
 * practice). Validated by shape and length rather than as a strict UUID, for
 * the same reason `attributionSchema` does: the client has a documented
 * fallback generator for browsers without a secure context, and the two must
 * agree. Absent is fine — an unattributed event is still a countable event.
 */
const sessionIdSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9_-]{8,64}$/)
  .optional();

export const eventSchema = z
  .object({
    eventName: z.enum(EVENT_NAMES),
    /** Site-relative path the event happened on. Never a full URL. */
    path: z
      .string()
      .trim()
      .max(EVENT_LIMITS.path)
      .regex(/^\/[^\s]*$/, 'Path must be site-relative.')
      .optional(),
    sessionId: sessionIdSchema,
    properties: eventPropertiesSchema.optional(),
  })
  .strict();

export type EventInput = z.infer<typeof eventSchema>;
export type EventProperties = z.infer<typeof eventPropertiesSchema>;
